import { useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK } from "@/lib/app";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ComposedChart, Line, Legend,
} from "recharts";
import { Sun, CalendarBlank, Target, TrendUp, TrendDown, CheckCircle, WarningCircle, NotePencil, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Slovak short date format: "Pi 26.6." */
function fmtShortSk(dateIso, weekdayIdx) {
  // dateIso: "2026-06-26"
  const parts = dateIso.split("-");
  if (parts.length !== 3) return dateIso;
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  const wd = WEEKDAYS_SK[weekdayIdx]?.slice(0, 2) || "";
  return `${wd} ${d}.${m}.`;
}

// ─── A. Today ────────────────────────────────────────────────
function TodayCard({ storeId, trainingDays }) {
  const [target, setTarget] = useState(todayIso());
  const tdQs = trainingDays ? `&training_days=${trainingDays}` : "";
  const { data } = useSWR(`/stores/${storeId}/forecast/today?target=${target}${tdQs}`, fetcher);

  if (!data) return <div className="card-flat p-6 text-sm text-[var(--text-muted)]">Načítavam predikciu pre dnes…</div>;
  if (data.empty) {
    return (
      <div className="card-flat p-6">
        <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
          <Sun size={18} weight="fill" className="text-[var(--brand)]" /> Dnešok
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-2">{data.reason}</p>
      </div>
    );
  }

  const s = data.summary;
  const chartData = data.hours.map((h) => ({
    hour: h.hour,
    label: `${h.hour}-${h.hour + 1}`,
    pred_revenue: h.pred_revenue,
    actual_revenue: h.actual_revenue,
    recommended: h.recommended_employees,
    actual: h.actual_employees,
    plan: h.planned_employees,
    min: h.min_required,
  }));

  return (
    <div className="card-flat p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
            <Sun size={18} weight="fill" className="text-[var(--brand)]" /> Dnešok
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Predikcia tržieb a odporúčaného obsadenia podľa rovnakého dňa v týždni (`{WEEKDAYS_SK[data.weekday]}`)
            z posledných {data.training_period.days} dní histórie.
          </p>
        </div>
        <input
          type="date"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="text-xs px-2 py-1 rounded border border-[var(--border-default)] bg-white tabular"
          data-testid="forecast-today-date"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Pred. tržba</p>
          <p className="font-display text-xl font-bold tabular text-[var(--brand)]">{fmtEur(s.predicted_revenue)}</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Odpor. obsadenie</p>
          <p className="font-display text-xl font-bold tabular text-[var(--success)]">{fmtNum(s.recommended_employees, 1)} h</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Realita zatiaľ</p>
          <p className="font-display text-xl font-bold tabular">{fmtEur(s.actual_revenue_so_far)}</p>
          <p className="text-[10px] text-[var(--text-muted)]">
            {s.actual_hours_imported} hod · {fmtNum(s.actual_employees_so_far, 1)} os.hod odpracovaných
          </p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Plán</p>
          {s.plan_employees ? (
            <p className="font-display text-xl font-bold tabular text-[var(--warning)]">{fmtNum(s.plan_employees, 1)} h</p>
          ) : (
            <p className="text-xs text-[var(--text-muted)] mt-2">žiadny plán pre dnes</p>
          )}
        </div>
      </div>

      <h4 className="text-xs font-semibold mb-1 text-[var(--text-secondary)]">Hodinový profil — predikcia × realita × plán</h4>
      <p className="text-[11px] text-[var(--text-muted)] mb-2 leading-relaxed">
        <span className="inline-block w-2 h-2 rounded-full bg-[#C78220] mr-1"></span> Pred. tržba (predikovaná) ·
        <span className="inline-block w-2 h-2 rounded-full bg-[#3E7B52] mx-1"></span> Realita tržba (skutočne nablokované) ·
        <span className="inline-block w-3 h-[2px] bg-[#2563EB] mx-1 align-middle"></span> <strong>Odporúčaní ľudia</strong> = ekonomické tlmenie: slabé hodiny sledujú strict math (↓ úspora), peak hodiny iba 50% nad denným priemerom (↓ nepretláča). Cieľ: maximalizovať profit, nie kopírovať tržbu ·
        <span className="inline-block w-3 h-[2px] bg-[#A78BFA] mx-1 align-middle" style={{borderTop:'2px dashed #A78BFA'}}></span> Plán (z tvojho podkladu cez „Import dát&ldquo;) ·
        <span className="inline-block w-3 h-[2px] bg-[#1A1F1C] mx-1 align-middle"></span> Reálne ľudia = priemer historickej obsadenosti.
        <br/>
        <strong>Ako čítať:</strong> ak modrá je <em>pod</em> čiernou → historicky si pretiahnutý, možná úspora.
        Ak je modrá <em>nad</em> čiernou → historicky podstavený, pridať ľudí by zvýšilo tržbu.
        <br/>
        <strong>Tip na ladenie:</strong> ak peak modrej je vyšší než realisticky chcete na hodinu, zvýš <em>požadovaný výkon</em> v konfigurácii strediska (napr. 130 €/os.hod namiesto 100). Pri 130 €/os.hod pri tržbe 780 € peak = 6 ľudí.
      </p>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}€`} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
          <Tooltip
            contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
            formatter={(v, n) => {
              if (n.includes("tržba") || n.includes("revenue")) return [fmtEur(v), n];
              return [fmtNum(v, 1), n];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="pred_revenue" fill="#C78220" name="Pred. tržba" radius={[3, 3, 0, 0]} />
          <Bar yAxisId="left" dataKey="actual_revenue" fill="#3E7B52" name="Realita tržba" radius={[3, 3, 0, 0]} />
          <Line yAxisId="right" type="monotone" dataKey="recommended" stroke="#2563EB" strokeWidth={2} name="Odporúčaní ľudia" dot={false} connectNulls={false} />
          <Line yAxisId="right" type="linear" dataKey="plan" stroke="#A78BFA" strokeWidth={2} strokeDasharray="4 2" name="Plán ľudia" dot={{ r: 3 }} connectNulls={false} />
          <Line yAxisId="right" type="monotone" dataKey="actual" stroke="#1A1F1C" strokeWidth={2} name="Reálne ľudia (historicky)" dot={{ r: 2 }} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── B. Next 7 days ──────────────────────────────────────────
function Next7Card({ storeId, trainingDays }) {
  const [days, setDays] = useState(7);
  const tdQs = trainingDays ? `&training_days=${trainingDays}` : "";
  const { data } = useSWR(`/stores/${storeId}/forecast/next7?days=${days}${tdQs}`, fetcher);

  if (!data) return <div className="card-flat p-6 text-sm text-[var(--text-muted)]">Načítavam 7-dňovú predikciu…</div>;
  if (data.empty) {
    return (
      <div className="card-flat p-6">
        <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
          <CalendarBlank size={18} weight="fill" className="text-[var(--brand)]" /> Najbližšie {days} dní
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-2">{data.reason}</p>
      </div>
    );
  }

  const chartData = data.days.map((d) => ({
    label: fmtShortSk(d.date, d.weekday),
    revenue: d.pred_revenue_total,
    reco: d.pred_person_hours,
    plan: d.plan_total_employees,
  }));

  const hasAnyPlan = chartData.some((d) => d.plan != null && d.plan > 0);

  return (
    <div className="card-flat p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
            <CalendarBlank size={18} weight="fill" className="text-[var(--brand)]" /> Najbližšie {days} dní
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Denná prognóza tržieb a odporúčaného obsadenia.{" "}
            <span className="inline-block w-3 h-[2px] bg-[#2563EB] mx-1 align-middle"></span><strong>Modrá</strong> = ekonomické tlmenie pri {fmtEur(data.required_performance)}/os.hod. Slabé hodiny klesajú podľa strict math (úspora), peak hodiny iba +50% nad denným priemerom (netlačíme zbytočne).{" "}
            {hasAnyPlan ? (
              <>
                <span className="inline-block w-3 h-[2px] bg-[#1A1F1C] mx-1 align-middle" style={{borderTop:'2px dashed #1A1F1C'}}></span><strong>Čierna prerušovaná</strong> = osobohodiny z tvojho plánu (cez „Import dát&ldquo;). Body bez plánu nie sú spojené.
              </>
            ) : (
              <span className="italic">Pre tieto dni nie je v systéme žiadny plán z tvojich podkladov.</span>
            )}
            {" "}<strong>Tip:</strong> ak je modrá bodka pre daný deň výrazne pod tvojím plánom, znamená to, že na ten deň môžete ubrať osobohodiny; ak naopak nad plánom — uvažuj o posile.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="text-xs px-2 py-1 rounded border border-[var(--border-default)] bg-white"
          data-testid="forecast-next7-days"
        >
          <option value="3">3 dni</option>
          <option value="7">7 dní</option>
          <option value="14">14 dní</option>
          <option value="30">30 dní</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Pred. tržba spolu</p>
          <p className="font-display text-xl font-bold tabular text-[var(--brand)]">{fmtEur(data.summary.total_predicted_revenue)}</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Odpor. obsadenie</p>
          <p className="font-display text-xl font-bold tabular text-[var(--success)]">{fmtNum(data.summary.total_recommended_personhours, 0)} h</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Priemer/deň</p>
          <p className="font-display text-xl font-bold tabular">{fmtEur(data.summary.total_predicted_revenue / data.summary.days)}</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}€`} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
          <Tooltip contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
            formatter={(v, n) => (n === "Pred. tržba" ? [fmtEur(v), n] : [fmtNum(v, 1), n])} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="revenue" fill="#C78220" name="Pred. tržba" radius={[3, 3, 0, 0]} />
          <Line yAxisId="right" type="monotone" dataKey="reco" stroke="#2563EB" strokeWidth={2} name="Odporúčané h" dot connectNulls={false} />
          <Line yAxisId="right" type="linear" dataKey="plan" stroke="#1A1F1C" strokeWidth={2} strokeDasharray="4 2" name="Plán h" dot={{ r: 4 }} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── C. Monthly goal ─────────────────────────────────────────
function GoalCard({ storeId }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const { data, mutate } = useSWR(`/stores/${storeId}/forecast/goal?year=${year}&month=${month}`, fetcher);
  const { data: plan, mutate: mutatePlan } = useSWR(
    `/stores/${storeId}/forecast/goal-plan?year=${year}&month=${month}`,
    fetcher,
  );
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState("");

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  const saveTarget = async () => {
    const v = parseFloat(target);
    if (isNaN(v) || v < 0) {
      toast.error("Zadaj kladné číslo (alebo 0 pre zrušenie cieľa)");
      return;
    }
    try {
      await api.put(`/stores/${storeId}/forecast/goal`, { year, month, target_revenue: v });
      toast.success(v > 0 ? "Cieľ uložený" : "Cieľ zrušený");
      setEditing(false);
      mutate();
      mutatePlan();
    } catch {
      toast.error("Nepodarilo sa uložiť cieľ");
    }
  };

  if (!data) return <div className="card-flat p-6 text-sm text-[var(--text-muted)]">Načítavam…</div>;

  const hasTarget = data.target_revenue > 0;
  const progressColor = data.progress_pct >= 100 ? "#3E7B52" : data.on_track ? "#C78220" : "#BA3F2B";
  const verdictIcon = data.progress_pct >= 100 ? CheckCircle : data.on_track ? TrendUp : WarningCircle;
  const Verdict = verdictIcon;
  const verdictText = data.progress_pct >= 100 ? "Cieľ splnený" : data.on_track ? "V tempe" : "Pod tempo";

  return (
    <div className="space-y-4">
      <div className="card-flat p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
              <Target size={18} weight="fill" className="text-[var(--brand)]" /> Mesačný cieľ
            </h3>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              Nastavte si plánovaný mesačný obrat, sledujeme priebeh a koľko € denne treba do konca mesiaca.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="text-xs px-2 py-1 rounded border border-[var(--border-default)] bg-white">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
              ))}
            </select>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="text-xs px-2 py-1 rounded border border-[var(--border-default)] bg-white">
              {Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {hasTarget && !editing && (
              <button
                onClick={() => { setEditing(true); setTarget(String(data.target_revenue)); }}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="goal-edit-button"
                title="Upraviť cieľ"
              >
                <NotePencil size={12} /> Upraviť cieľ
              </button>
            )}
          </div>
        </div>

        {!hasTarget && !editing && (
          <div className="text-center py-6">
            <p className="text-sm text-[var(--text-secondary)] mb-3">Pre {monthKey} nie je nastavený cieľ.</p>
            <button onClick={() => { setEditing(true); setTarget(""); }} className="btn-primary text-sm" data-testid="goal-set-button">Nastaviť cieľ</button>
          </div>
        )}

        {editing && (
          <div className="bg-[var(--bg-page)] rounded p-3 mb-4">
            <label className="label-eyebrow block mb-2">Cieľ tržby pre {monthKey} (€)</label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="number"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="napr. 25000"
                className="flex-1 min-w-[160px] text-sm px-3 py-2 rounded border border-[var(--border-default)] bg-white tabular"
                data-testid="goal-target-input"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") saveTarget(); if (e.key === "Escape") setEditing(false); }}
              />
              <span className="text-sm text-[var(--text-muted)]">€</span>
              <button onClick={saveTarget} className="btn-primary text-sm" data-testid="save-goal">Uložiť</button>
              <button onClick={() => setEditing(false)} className="btn-ghost text-sm" data-testid="cancel-goal-edit">Zrušiť</button>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-2">
              Tip: zadaj 0 pre odstránenie cieľa. Enter = uložiť, Esc = zrušiť.
            </p>
          </div>
        )}

        {hasTarget && !editing && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="card-flat p-3">
                <p className="label-eyebrow mb-1">Cieľ</p>
                <p className="font-display text-xl font-bold tabular">{fmtEur(data.target_revenue)}</p>
              </div>
              <div className="card-flat p-3">
                <p className="label-eyebrow mb-1">Doteraz</p>
                <p className="font-display text-xl font-bold tabular" style={{ color: progressColor }}>{fmtEur(data.actual_revenue)}</p>
                <p className="text-[10px] text-[var(--text-muted)]">{data.progress_pct.toFixed(1)} % cieľa</p>
              </div>
              <div className="card-flat p-3">
                <p className="label-eyebrow mb-1">Zostáva</p>
                <p className="font-display text-xl font-bold tabular">{fmtEur(Math.max(0, data.target_revenue - data.actual_revenue))}</p>
                <p className="text-[10px] text-[var(--text-muted)]">{data.days_remaining} dní do konca</p>
              </div>
              <div className="card-flat p-3">
                <p className="label-eyebrow mb-1">Tempo</p>
                <p className="text-xs inline-flex items-center gap-1" style={{ color: progressColor }}>
                  <Verdict size={14} weight="fill" /> {verdictText}
                </p>
                <p className="font-display text-base font-semibold tabular mt-1">{fmtEur(Math.max(0, data.needed_per_day_remaining))}<span className="text-xs"> /deň treba</span></p>
                <p className="text-[10px] text-[var(--text-muted)]">priemer doteraz: {fmtEur(data.avg_per_day_actual)}/deň</p>
              </div>
            </div>
            <div className="relative h-6 bg-[var(--bg-page)] rounded overflow-hidden">
              <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${Math.min(100, data.progress_pct)}%`, background: progressColor }} />
              <div className="absolute inset-0 flex items-center justify-center text-xs font-medium" style={{ mixBlendMode: data.progress_pct < 50 ? "normal" : "difference", color: data.progress_pct < 50 ? "#1A1F1C" : "white" }}>
                {data.progress_pct.toFixed(1)} %
              </div>
            </div>
          </>
        )}
      </div>

      {/* Goal-achievement plan */}
      {hasTarget && plan && !plan.empty && plan.verdict !== "achieved" && plan.verdict !== "month_over" && (
        <GoalPlanCard plan={plan} />
      )}
      {hasTarget && plan?.verdict === "achieved" && (
        <div className="card-flat p-4 inline-flex items-center gap-3 bg-[var(--success)]/5 border border-[var(--success)]/30 w-full">
          <CheckCircle size={24} weight="fill" className="text-[var(--success)] shrink-0" />
          <p className="text-sm text-[var(--text-primary)]">{plan.message}</p>
        </div>
      )}
      {hasTarget && plan?.verdict === "month_over" && (
        <div className="card-flat p-4 inline-flex items-center gap-3 bg-[var(--warning)]/5 border border-[var(--warning)]/30 w-full">
          <WarningCircle size={24} weight="fill" className="text-[var(--warning)] shrink-0" />
          <p className="text-sm text-[var(--text-primary)]">{plan.message}</p>
        </div>
      )}
    </div>
  );
}

// ─── C2. Goal-achievement plan ───────────────────────────────
function GoalPlanCard({ plan }) {
  const [expanded, setExpanded] = useState(false);
  const tone = plan.verdict === "in_pace" ? "var(--success)" : plan.verdict === "stretch" ? "var(--warning)" : "var(--critical)";
  const VerdictIcon = plan.verdict === "in_pace" ? CheckCircle : plan.verdict === "stretch" ? TrendUp : Lightning;
  const verdictLabel = plan.verdict === "in_pace" ? "V tempe" : plan.verdict === "stretch" ? "Stretch — dosiahnuteľné" : "Ambiciózny cieľ";

  // Show only top days with biggest gap unless expanded
  const sortedDays = [...(plan.days || [])].sort((a, b) => (b.gap || 0) - (a.gap || 0));
  const visibleDays = expanded ? sortedDays : sortedDays.slice(0, 7);

  return (
    <div className="card-flat p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-[var(--brand)]" /> Ako dosiahnuť cieľ
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Predikcia + alokácia chýbajúcej tržby cez zostatok mesiaca. Silnejšie dni dostanú väčšiu časť push-u.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: `${tone}15`, color: tone }}>
          <VerdictIcon size={14} weight="fill" /> {verdictLabel}
        </div>
      </div>

      <p className="text-sm text-[var(--text-primary)] bg-[var(--bg-page)] rounded p-3 mb-4">
        {plan.message}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Predikcia zostatku</p>
          <p className="font-display text-lg font-bold tabular">{fmtEur(plan.total_predicted_remaining)}</p>
          <p className="text-[10px] text-[var(--text-muted)]">čistá projekcia bez intervencie</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Chýba navyše</p>
          <p className="font-display text-lg font-bold tabular" style={{ color: tone }}>{fmtEur(plan.gap_to_close)}</p>
          <p className="text-[10px] text-[var(--text-muted)]">nad rámec predikcie</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Extra os.hodiny</p>
          <p className="font-display text-lg font-bold tabular">{fmtNum(plan.total_extra_personhours, 1)} h</p>
          <p className="text-[10px] text-[var(--text-muted)]">pri výkone {fmtEur(plan.required_performance)}/h</p>
        </div>
        <div className="card-flat p-3">
          <p className="label-eyebrow mb-1">Mal by ste dosiahnuť</p>
          <p className="font-display text-lg font-bold tabular">{fmtEur(plan.expected_to_reach)}</p>
          <p className="text-[10px] text-[var(--text-muted)]">aktuálne + predikcia</p>
        </div>
      </div>

      <h4 className="text-xs font-semibold text-[var(--text-secondary)] mb-2">
        Per-deň návrh ({plan.days.length} dní)
        {sortedDays.length > 7 && (
          <button onClick={() => setExpanded(!expanded)} className="ml-2 text-[var(--brand)] hover:underline" data-testid="goal-plan-toggle">
            {expanded ? "zobraziť TOP 7" : `zobraziť všetkých ${sortedDays.length}`}
          </button>
        )}
      </h4>
      <div className="overflow-x-auto scroll-area">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--text-secondary)] text-xs">
              <th className="py-2 pr-3">Dátum</th>
              <th className="py-2 pr-3">Deň</th>
              <th className="py-2 pr-3 text-right">Pred. tržba</th>
              <th className="py-2 pr-3 text-right">Požadovaná</th>
              <th className="py-2 pr-3 text-right">Gap navyše</th>
              <th className="py-2 pr-3 text-right">Extra h</th>
            </tr>
          </thead>
          <tbody className="tabular">
            {visibleDays.map((d) => (
              <tr key={d.date} className="border-t border-[var(--border-default)]">
                <td className="py-1.5 pr-3">{d.date}</td>
                <td className="py-1.5 pr-3">{d.weekday_name}</td>
                <td className="py-1.5 pr-3 text-right text-[var(--text-secondary)]">{fmtEur(d.pred_revenue)}</td>
                <td className="py-1.5 pr-3 text-right font-semibold">{fmtEur(d.required_revenue)}</td>
                <td className="py-1.5 pr-3 text-right" style={{ color: d.gap > 0 ? tone : "var(--text-muted)" }}>
                  {d.gap > 0 ? `+${fmtEur(d.gap)}` : "—"}
                </td>
                <td className="py-1.5 pr-3 text-right" style={{ color: d.extra_personhours_needed > 0 ? tone : "var(--text-muted)" }}>
                  {d.extra_personhours_needed > 0 ? `+${fmtNum(d.extra_personhours_needed, 1)} h` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[var(--text-muted)] mt-3">
        💡 Najsilnejšie dni v týždni (zvyčajne piatok/sobota) sú najlepší kandidát na promo akcie alebo predĺženú prevádzku — keď ich potlačíš, dosiahneš cieľ s najmenším nasadením extra hodín.
      </p>
    </div>
  );
}

// ─── Wrapper ──────────────────────────────────────────────────
const TRAINING_PRESETS = [
  { id: "default", label: "Default (zo settings)", days: null },
  { id: "2m", label: "Posledné 2 mesiace", days: 60 },
  { id: "3m", label: "Posledný kvartál", days: 90 },
  { id: "6m", label: "Posledný polrok", days: 180 },
  { id: "1y", label: "Posledný rok", days: 365 },
  { id: "all", label: "Celá história", days: 0 },
];

export default function Predikcia({ storeId }) {
  const [presetId, setPresetId] = useState("default");
  const preset = TRAINING_PRESETS.find((p) => p.id === presetId) || TRAINING_PRESETS[0];
  // `null` (default) → omit param; `0` (all) → use very large window (~20 years)
  const trainingDays = preset.days === null ? null : (preset.days === 0 ? 7300 : preset.days);

  return (
    <div className="space-y-5">
      <div className="card-flat p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <p className="label-eyebrow mb-1">Tréningové okno predikcie</p>
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              Z akého rozsahu historických dát sa má model učiť. <strong>Nezávislé</strong> od globálneho zobrazovacieho obdobia.
              Príklad: ak je leto a zima ti skresľuje vzorce, vyberte „Posledné 2 mesiace“ — model bude predikovať len z aktuálnej sezóny.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {TRAINING_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPresetId(p.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  presetId === p.id
                    ? "bg-[var(--brand)] text-white border-[var(--brand)]"
                    : "bg-white text-[var(--text-secondary)] border-[var(--border-default)] hover:border-[var(--brand)] hover:text-[var(--text-primary)]"
                }`}
                data-testid={`training-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <TodayCard storeId={storeId} trainingDays={trainingDays} />
      <Next7Card storeId={storeId} trainingDays={trainingDays} />
      <GoalCard storeId={storeId} />
    </div>
  );
}
